import Loader from '@/components/Loader';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  loading?: boolean;
  footer?: React.ReactNode;
  widthClass?: string;
}

const modalRoot = typeof document !== 'undefined' ? document.body : null;

export default function Modal({
  open,
  onClose,
  title,
  children,
  loading = false,
  footer,
  widthClass = 'max-w-xl',
}: ModalProps) {
  const [shouldRender, setShouldRender] = useState(open);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      document.body.style.overflow = 'hidden';
      // Small delay to trigger transition
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else {
      setIsAnimating(false);
      document.body.style.overflow = '';
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300); // Match transition duration
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!shouldRender || !modalRoot) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 font-spectral transition-opacity duration-300 ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-lg shadow-lg relative w-full ${widthClass} max-h-[90vh] flex flex-col overflow-hidden transition-all duration-300 transform ${
          isAnimating ? 'scale-100 translate-y-0 opacity-100' : 'scale-95 translate-y-4 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="flex justify-between items-center bg-[#001B6B] text-white px-6 py-4 rounded-t-lg shrink-0">
            <h2 className="font-24 font-semibold">{title}</h2>
            <button
              onClick={onClose}
              className="text-white text-4xl font-bold hover:text-gray-200 cursor-pointer leading-none"
            >
              ×
            </button>
          </div>
        )}

        <div className="p-6 text-black font-lato overflow-y-auto flex-1 min-h-0">
          {loading ? <Loader /> : children}
        </div>

        {footer && <div className="px-6 py-4 border-t border-gray-200 shrink-0">{footer}</div>}
      </div>
    </div>,
    modalRoot
  );
}
